import ReportMetric from '@app/components/reports/components/report-metric';
import { useGetCompanyReportPreview } from '@shared/api/hooks/ccf/reports';
import SimplePieChart from '@shared/components/chart/simple-pie-chart';
import Text from '@shared/components/content/text';
import { ChartConfig } from '@shared/components/ui/chart';
import {
  cn,
  formatNumber,
  roundNumberToNDecimalPlaces,
} from '@shared/lib/utils';
import { useState } from 'react';
import PreviewSheet from '../../../components/preview/preview-sheet';
import PreviewSheetSection from '../../../components/preview/preview-sheet-chart-section';
import PreviewSheetTableSection from '../../../components/preview/preview-sheet-table-section';
import { useCCF } from '../ccf-context';

export default function CCFHubPreviewSheet() {
  const [isOpen, setIsOpen] = useState(false);
  const { title, uuid: reportUuid, scope3Supported } = useCCF();

  const { data: companyReportPreview, status } = useGetCompanyReportPreview({
    companyReportUuid: reportUuid!,
    enabled: isOpen,
  });

  const scope1Emissions = companyReportPreview?.emissionsByScope.find(
    (scope) => scope.scopeName === 'Scope 1'
  )?.totalScopeEmissionsTonnes;

  const scope2Emissions = companyReportPreview?.emissionsByScope.find(
    (scope) => scope.scopeName === 'Scope 2'
  )?.totalScopeEmissionsTonnes;

  const scope3Emissions = companyReportPreview?.emissionsByScope.find(
    (scope) => scope.scopeName === 'Scope 3'
  )?.totalScopeEmissionsTonnes;

  const totalCompanyEmissionsTonnes = roundNumberToNDecimalPlaces(
    scope3Supported
      ? scope1Emissions! + scope2Emissions! + scope3Emissions!
      : scope1Emissions! + scope2Emissions!,
    2,
    true
  );

  const highestEmissionsActivities =
    companyReportPreview?.highestEmissionsActivities;

  const emissionsByScope =
    companyReportPreview?.emissionsByScope
      .filter((item) => item.scopeName !== 'Out of scope')
      .map((scope, idx) => {
        if (!scope3Supported && scope.scopeName === 'Scope 3') {
          return {
            scope: scope.scopeName,
            emissions: 0,
            fill: 'var(--gray-300)',
          };
        }

        return {
          scope: scope.scopeName,
          emissions: scope.totalScopeEmissionsTonnes,
          fill: `var(--chart-${idx + 2})`,
        };
      }) || [];

  const chartConfig = {
    emissions: {
      label: 'Emissions',
    },
    'scope 1': {
      label: 'Scope 1',
      color: 'var(--chart-2)',
    },
    'scope 2': {
      label: 'Scope 2',
      color: 'var(--chart-3)',
    },
    'scope 3': {
      label: 'Scope 3',
      color: 'var(--chart-4)',
    },
  } satisfies ChartConfig;

  return (
    <PreviewSheet
      status={status}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={
        <div>
          <Text className="text-md font-semibold">{title}</Text>
        </div>
      }
    >
      <div className="space-y-2">
        <ReportMetric
          title="Total company emissions"
          titleClassName="font-default font-medium text-md text-primary-600"
          value={formatNumber(totalCompanyEmissionsTonnes)}
          unit="tCO2e"
          size="lg"
        />
      </div>
      <div className="flex items-center px-2">
        <div className="space-y-2">
          {emissionsByScope!.map((scope, idx) => {
            return (
              <ReportMetric
                key={idx}
                title={scope.scope}
                value={
                  !scope3Supported && scope.scope === 'Scope 3'
                    ? 'Not included'
                    : scope.emissions
                }
                unit={
                  !scope3Supported && scope.scope === 'Scope 3' ? '' : 'tCO2e'
                }
                titleClassName="text-normal font-base font-normal text-primary-600"
                valueClassName={cn('text-2xl font-semibold', {
                  'text-[var(--chart-2)]': scope.scope === 'Scope 1',
                  'text-[var(--chart-3)]': scope.scope === 'Scope 2',
                  'text-[var(--chart-4)]': scope.scope === 'Scope 3',
                  'text-base text-gray-600':
                    scope.scope === 'Scope 3' && !scope3Supported,
                })}
                className="space-y-1"
              />
            );
          })}
        </div>
        <div className="w-10 flex-1">
          <SimplePieChart
            nameKey="scope"
            dataKey="emissions"
            chartData={emissionsByScope!.reverse()}
            chartConfig={chartConfig}
            innerRadius={45}
            className="h-44 w-full"
            margin={{ top: 0, right: 0, bottom: 0, left: -0 }}
          />
        </div>
      </div>

      <PreviewSheetSection title="Highest emissions activities">
        <PreviewSheetTableSection
          columnName="Activity"
          items={highestEmissionsActivities!}
        />
      </PreviewSheetSection>
    </PreviewSheet>
  );
}
